import { ApiError } from "../../shared/api/api-error";
import { requireCsrfToken } from "../../shared/api/csrf";
import {
  authFailureSchema,
  authSuccessSchema,
  logoutSuccessSchema,
  type AuthSessionData,
  type LoginInput
} from "./auth-contracts";

const AUTH_BASE_PATH = "/api/auth";

export const authApi = {
  login(input: LoginInput, signal?: AbortSignal): Promise<AuthSessionData> {
    return authRequest("/login", {
      method: "POST",
      body: input,
      ...(signal ? { signal } : {}),
      includeCsrf: false
    });
  },

  session(signal?: AbortSignal): Promise<AuthSessionData> {
    return authRequest("/session", signal ? { signal } : {});
  },

  async logout(signal?: AbortSignal): Promise<void> {
    const headers = new Headers({
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": requireCsrfToken()
    });
    let response: Response;
    try {
      response = await fetch(`${AUTH_BASE_PATH}/logout`, {
        method: "POST",
        credentials: "same-origin",
        headers,
        body: "{}",
        ...(signal ? { signal } : {})
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
      throw new ApiError({
        status: 0,
        code: "NETWORK_ERROR",
        message: "Não foi possível encerrar a sessão administrativa.",
        cause
      });
    }

    const payload = await parseResponse(response);
    const failure = authFailureSchema.safeParse(payload);
    if (failure.success) {
      throw new ApiError({
        status: response.status,
        code: failure.data.error.code,
        message: failure.data.error.message,
        requestId: failure.data.error.requestId
      });
    }
    const success = logoutSuccessSchema.safeParse(payload);
    if (!response.ok || !success.success) {
      throw new ApiError({
        status: response.status,
        code: "INVALID_API_RESPONSE",
        message: "O serviço não confirmou o encerramento da sessão."
      });
    }
  }
} as const;

interface AuthRequestOptions {
  method?: "GET" | "POST";
  body?: LoginInput;
  signal?: AbortSignal;
  includeCsrf?: boolean;
}

async function authRequest(path: string, options: AuthRequestOptions): Promise<AuthSessionData> {
  const headers = new Headers({ Accept: "application/json" });
  const init: RequestInit = {
    method: options.method ?? "GET",
    credentials: "same-origin",
    headers,
    ...(options.signal ? { signal: options.signal } : {})
  };

  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    init.body = JSON.stringify(options.body);
  }

  if (options.includeCsrf) {
    let token: string;
    try {
      token = requireCsrfToken();
    } catch (cause) {
      throw new ApiError({
        status: 0,
        code: "CSRF_TOKEN_UNAVAILABLE",
        message: "A sessão segura não está pronta. Atualize a página e tente novamente.",
        cause
      });
    }
    headers.set("X-CSRF-Token", token);
  }

  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE_PATH}${path}`, init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError({
      status: 0,
      code: "NETWORK_ERROR",
      message: "Não foi possível conectar ao painel administrativo.",
      cause
    });
  }

  const payload = await parseResponse(response);
  const failure = authFailureSchema.safeParse(payload);
  if (failure.success) {
    throw new ApiError({
      status: response.status,
      code: failure.data.error.code,
      message: failure.data.error.message,
      requestId: failure.data.error.requestId
    });
  }

  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      code: "HTTP_ERROR",
      message: `O serviço de autenticação respondeu com HTTP ${String(response.status)}.`
    });
  }

  const success = authSuccessSchema.safeParse(payload);
  if (!success.success) {
    throw new ApiError({
      status: response.status,
      code: "INVALID_API_RESPONSE",
      message: "O serviço de autenticação retornou uma resposta inválida.",
      cause: success.error
    });
  }

  return success.data.data;
}

async function parseResponse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch (cause) {
    throw new ApiError({
      status: response.status,
      code: "INVALID_API_RESPONSE",
      message: "O serviço de autenticação retornou JSON inválido.",
      cause
    });
  }
}
